/**
 * Pure helpers for sequential context-passing chunked Gemini transcription of
 * long (>8 min) Afrikaans recordings.
 *
 * Why this exists: AssemblyAI's Afrikaans is tier-3 garbage and a single Gemini
 * call 504s past ~8 min (Vercel's 60s function budget). The only path that
 * yields clean Afrikaans for 50–180 min meetings is to slice the audio into
 * overlapping windows and transcribe them in order, feeding each chunk the
 * previous chunk's tail so Gemini keeps speaker numbering consistent and skips
 * the overlap instead of re-transcribing it — so no post-hoc stitch / dedup /
 * speaker reconciliation is needed. Empirically validated 2026-06-01.
 *
 * The audio I/O (decode / resample / slice / API call) lives in
 * useTranscription.ts; everything here is pure and unit-tested.
 *
 * See docs/dtp-runs/2026-06-01-afrikaans-dutch-and-inp.md and
 * docs/handoff/2026-06-01-afrikaans-long-meeting-chunked-gemini.md.
 */

/** ~90s windows with ~15s overlap, resampled to 16kHz mono. */
export const CHUNK_WINDOW_S = 90;
export const CHUNK_OVERLAP_S = 15;
/**
 * 16kHz keeps a 90s mono 16-bit WAV at ~2.88MB → base64 ~3.84MB, under Vercel's
 * ~4.5MB JSON body limit. Do NOT raise the window to 3 min at 16kHz — that
 * exceeds the body limit; for bigger chunks drop to 8kHz or use the Files-API.
 */
export const CHUNK_SAMPLE_RATE = 16000;

export interface ChunkWindow {
  /** Global start time of the window, in seconds. */
  start: number;
  /** Global end time of the window, in seconds (clamped to total duration). */
  end: number;
  /** Window index (0-based). */
  index: number;
}

/**
 * Split a recording of `totalDuration` seconds into overlapping windows.
 *
 * Each window is `windowS` long; consecutive windows advance by
 * `windowS - overlapS`, so each shares `overlapS` of audio with the previous
 * one. The final window is clamped to `totalDuration`. The overlap is what the
 * continuation hint tells Gemini to skip, giving gap-free, dup-free coverage
 * once each chunk's local timestamps are offset by `window.start`.
 */
export function computeChunkWindows(
  totalDuration: number,
  windowS: number = CHUNK_WINDOW_S,
  overlapS: number = CHUNK_OVERLAP_S,
): ChunkWindow[] {
  if (!(totalDuration > 0)) return [];
  const step = Math.max(1, windowS - overlapS);
  const windows: ChunkWindow[] = [];
  let index = 0;
  for (let start = 0; start < totalDuration; start += step) {
    const end = Math.min(start + windowS, totalDuration);
    windows.push({ start, end, index: index++ });
    if (end >= totalDuration) break; // last window reached the end — stop
  }
  return windows;
}

/**
 * Minimum plausible segment count for a chunk of `chunkDurationS` seconds,
 * used as the sparse-output retry threshold (~1 segment / 30s, floor 1).
 * A chunk that returns fewer than this is retried once.
 */
export function minPlausibleSegments(chunkDurationS: number): number {
  return Math.max(1, Math.floor(chunkDurationS / 30));
}

/**
 * Render the last `n` utterances as speaker-labelled lines for the next
 * chunk's continuation hint. Speaker ids are 0-based internally; displayed
 * 1-based ("Speaker 1") to match Gemini's own labelling convention.
 */
export function renderTailContext(
  utterances: ReadonlyArray<{ speaker: number; transcript: string }>,
  n: number = 4,
): string {
  return utterances
    .slice(-n)
    .map((u) => `Speaker ${u.speaker + 1}: ${u.transcript}`)
    .join('\n');
}

/**
 * Max attempts per chunk before giving up on that chunk. Transcribing a long
 * recording fires dozens of independent API calls; a single transient 504
 * (FUNCTION_INVOCATION_TIMEOUT) or 422 (per-chunk repetition) should retry that
 * one chunk, not abort the whole transcript. See DTP-002 2026-06-01 resilience run.
 */
export const CHUNK_MAX_ATTEMPTS = 3;

/**
 * Whether enough chunks failed that the whole transcript should be treated as a
 * failure (fail loud) rather than returned with gap markers. True if every chunk
 * failed, or strictly more than half did — a handful of gaps is acceptable, a
 * majority means the engine is down and the user must be told, not handed swiss
 * cheese presented as a result.
 */
export function tooManyChunksFailed(failed: number, total: number): boolean {
  if (total <= 0) return true;
  return failed === total || failed * 2 > total;
}

/**
 * Placeholder inserted for a chunk that exhausted all retries, keeping the
 * timeline intact without fabricating words. Honest gap, never a guess.
 */
export function gapMarkerText(): string {
  return '[inaudible — this segment could not be transcribed; please retry]';
}

/** Dependencies for {@link transcribeChunkWithRetry} (injected so the retry/gap
 *  logic is pure and unit-testable without Web Audio or the network). */
export interface ChunkRetryDeps<T> {
  /** Perform one transcription call for the chunk. */
  attempt: () => Promise<T>;
  /** Read the segment count from a result (used for the sparse-output retry). */
  segmentCount: (result: T) => number;
  /** Minimum plausible segments for this chunk — below it, retry. */
  minSegments: number;
  /** Max attempts (default {@link CHUNK_MAX_ATTEMPTS}). */
  maxAttempts?: number;
  /** Optional warn sink for retry diagnostics. */
  onWarn?: (msg: string) => void;
  /** Optional cancel check — must throw `PIPELINE_CANCELLED` to abort. */
  checkAborted?: () => void;
  /** Optional sleep (injected for tests); defaults to setTimeout backoff. */
  sleep?: (ms: number) => Promise<void>;
}

/**
 * Transcribe a single chunk with retry. Retries on a thrown error (transient
 * 504 `FUNCTION_INVOCATION_TIMEOUT` / 422 repetition) with linear backoff, and
 * on sparse output (under-transcription), up to `maxAttempts`. Returns the
 * result, or `null` when every attempt failed (caller inserts a gap). A
 * `PIPELINE_CANCELLED` error is re-thrown immediately so a user cancel is never
 * swallowed as a chunk failure.
 */
export async function transcribeChunkWithRetry<T>(deps: ChunkRetryDeps<T>): Promise<T | null> {
  const max = deps.maxAttempts ?? CHUNK_MAX_ATTEMPTS;
  const sleep = deps.sleep ?? ((ms: number) => new Promise<void>((r) => setTimeout(r, ms)));
  for (let attempt = 1; attempt <= max; attempt++) {
    deps.checkAborted?.();
    try {
      const result = await deps.attempt();
      if (deps.segmentCount(result) < deps.minSegments && attempt < max) {
        deps.onWarn?.(`sparse output (${deps.segmentCount(result)} < ${deps.minSegments}) — retrying`);
        continue; // re-request the same chunk
      }
      return result;
    } catch (err: unknown) {
      if (err instanceof Error && err.message === 'PIPELINE_CANCELLED') throw err;
      const msg = err instanceof Error ? err.message : String(err);
      if (attempt < max) {
        deps.onWarn?.(`attempt ${attempt} failed (${msg.slice(0, 70)}) — retrying`);
        await sleep(3000 * attempt); // linear backoff
      } else {
        deps.onWarn?.(`failed after ${max} attempts`);
      }
    }
  }
  return null;
}

/**
 * Build the continuation context prepended to the transcription prompt for
 * every chunk after the first. It carries the previous chunk's tail (with
 * speaker labels) so Gemini keeps speaker numbering consistent and skips the
 * overlapping audio rather than re-transcribing it.
 */
export function buildContinuationHint(
  prevTail: string,
  overlapS: number = CHUNK_OVERLAP_S,
): string {
  return [
    'CONTINUATION — this audio continues a longer recording that was split into parts.',
    'The previous part ended with these lines (keep the SAME Speaker numbering for the same people):',
    '',
    prevTail.trim(),
    '',
    `The first ~${overlapS} seconds of THIS audio repeats the end of the previous part.`,
    'Do NOT re-transcribe that overlap — start from the new speech that follows it.',
    'Keep speaker labels consistent with the lines above (Speaker 1 = the same person as before).',
    'Continue transcribing in Afrikaans wherever Afrikaans is spoken — never Dutch.',
    '',
  ].join('\n');
}
